/**
 * M4S-5618 (spec §17.18): every code of an option that carries several, for the matchers that
 * store one code per option.
 *
 * dedupeQuestions merges a later survey wave's codes into the option — or the matrix cell — they
 * mean, so an option can carry several codes, and the table rows count any of them. Live filters,
 * variable conditions and weighting targets store a single id per option, the catalog's
 * `optionIdentities` (CrosstabsWorkspace catalog). Each resolves that id to its whole option here, so
 * a later wave's respondents count in them exactly as they do in the rows.
 *
 * M4S-5777 (docs/pdlc/crosstabs-open-ends-tpbp.md §4, defect 12). A coded open-end net is not a
 * wave merge. Its `option_id` is every code beneath it — different answers, one union — and the
 * catalog lists each net before its members, so under "the first option to claim a code wins"
 * every member code resolved to its whole net: a filter on code 1 counted everyone in code 1's
 * net. And a net with no code of its own has its first member's code as `option_id[0]`, so the
 * net and that member shared one id in the filter, banner and Nest catalogs and in the banner's
 * column ids. Two rules, one home (this file):
 *  - `optionIdentities` names each option. It is `option_id[0]`, except on an open-end net whose
 *    `option_id[0]` is not its own code: that net is `oeNetHeadId(label)`. Nothing is stored
 *    differently (decision O1); only the id the pickers and the banner hand out changes.
 *  - `optionCodesOf` maps an open-end net's identity to all of its codes, and never a member's
 *    code to its net. A member code means that code alone.
 */

import type { CrosstabQuestionDto } from '@/lib/crosstab/question-formatter';

import type {
    CustomVariable,
    LiveFilter,
    NestMember,
    NestRecipe,
    VariableBucket,
    WeightScheme,
} from './crosstab-types';

/** Upper-cased qid → upper-cased id → every code of that id's option. Multi-code options only. */
export type OptionCodes = Map<string, Map<string, string[]>>;

/** The prefix of an open-end net's head id; `oe-frame.ts` gives a net with no codes the same id. */
export const OE_NET_HEAD_PREFIX = 'NET_';

/** The id that names an open-end net with no code of its own. Net labels are unique per question
 *  (`orderOeFrame` merges same-label nets), so the id is too. */
export const oeNetHeadId = (label: string): string => `${OE_NET_HEAD_PREFIX}${label}`;

type QuestionShape = Pick<CrosstabQuestionDto, 'isOpenEnd' | 'options'>;

const labelKey = (text: string | undefined): string =>
    (text ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Each option's identity, in option order. See the header: `option_id[0]`, except an open-end net
 * whose first id belongs to an option under it (a net with no code of its own), which is
 * `oeNetHeadId(label)`. Worked out from the question alone, whatever order its options are in.
 */
export function optionIdentities(question: QuestionShape): string[] {
    const options = question.options ?? [];
    if (!question.isOpenEnd) return options.map((o) => o.option_id[0] ?? '');

    // Every id held by an option that sits under a net, keyed by that net's label: a net's
    // `option_id[0]` is borrowed exactly when one of its children holds it.
    const heldUnder = new Map<string, Set<string>>();
    for (const o of options) {
        if (!o.question_group) continue;
        const key = labelKey(o.question_group);
        const held = heldUnder.get(key) ?? new Set<string>();
        for (const id of o.option_id) held.add(id.toUpperCase());
        heldUnder.set(key, held);
    }

    return options.map((o) => {
        const first = o.option_id[0] ?? '';
        if (!o.isNet || !first) return first;
        if (first.toUpperCase().startsWith(OE_NET_HEAD_PREFIX)) return first;
        const borrowed = heldUnder.get(labelKey(o.option))?.has(first.toUpperCase()) ?? false;
        return borrowed ? oeNetHeadId(o.option) : first;
    });
}

export function optionCodesOf(questions: CrosstabQuestionDto[]): OptionCodes {
    const out: OptionCodes = new Map();
    for (const q of questions) {
        let codes: Map<string, string[]> | undefined;
        const identities = q.isOpenEnd ? optionIdentities(q) : null;
        q.options.forEach((opt, i) => {
            const all = opt.option_id.map((id) => id.toUpperCase());
            if (identities && opt.isNet) {
                // An open-end net: its identity means the union of its codes. Its members are
                // left alone, so each still means its own code.
                const head = identities[i].toUpperCase();
                if (!head || (all.length < 2 && all[0] === head)) return;
                codes ??= new Map();
                codes.set(head, all);
                return;
            }
            if (all.length < 2) return;
            codes ??= new Map();
            for (const id of all) if (!codes.has(id)) codes.set(id, all);
        });
        if (codes) out.set(q.qid.toUpperCase(), codes);
    }
    return out;
}

/** Whether a respondent's answers to `qid` hold `code`, or any other code of its option. */
export function holdsCode(
    selected: Set<string> | undefined,
    qid: string,
    code: string,
    codes: OptionCodes | undefined,
): boolean {
    if (!selected || selected.size === 0) return false;
    const upper = code.trim().toUpperCase();
    const all = codes?.get(qid.toUpperCase())?.get(upper);
    return all ? all.some((id) => selected.has(id)) : selected.has(upper);
}

// ── A crosstab saved before open-end nets had their own ids ─────────────────────────────────────

/**
 * M4S-5777 (docs/pdlc/crosstabs-open-ends-tpbp.md §4.1). Before nets had their own ids, the
 * pickers gave a net with no code of its own the code it borrows from its first member, and a
 * crosstab that picked the net stored that code. The old catalog listed the net first, so the
 * picker, the filter chip and the condition editor all showed the net for that id. Read today, the
 * code means the member alone, and those crosstabs would quietly count fewer people.
 *
 * `migrateBorrowedOeIds` reads such a crosstab the way it was shown when it was built. It runs, in
 * memory, on a record or browser slot without the `oeNetIds` stamp (every save from this release on
 * carries it), before anything is computed or saved:
 *  - a live filter on a borrowed code whose stored option label is a borrowing net's names that
 *    net (`oeNetHeadId`); one whose label is the member's keeps the member;
 *  - a variable condition (`eq` / `neq`), a Nest member (its key and buckets, and the recipe's
 *    `dropped` and `names`, so `nestMatchesBuckets` still holds), a weighting target and a
 *    weighting group on a borrowed code name the FIRST net that borrows it: the option the old
 *    picker listed for that id. None of them stores a label that could say otherwise.
 * A banner rename keyed `<QID>:<borrowed code>` renamed the net's column and its first member's at
 * once, and now renames the member's alone. Nothing says which was meant, so it is left as it is
 * and reported in `ambiguousBannerKeys` for the workspace to say so.
 */
export type OeIdState = {
    liveFilters: LiveFilter[];
    customVariables: CustomVariable[];
    weightScheme: WeightScheme | null;
    bannerRenames: Record<string, string>;
};

export type OeIdMigration = Omit<OeIdState, 'bannerRenames'> & {
    /** Banner rename keys on a borrowed code: they used to rename the net's column too. */
    ambiguousBannerKeys: string[];
    /** Whether a filter, variable or weight was rewritten. */
    changed: boolean;
};

type BorrowingNet = { head: string; label: string };

/** Upper-cased qid → upper-cased borrowed code → the nets that borrow it, in option order. */
function borrowedOeCodes(
    questions: CrosstabQuestionDto[],
): Map<string, Map<string, BorrowingNet[]>> {
    const out = new Map<string, Map<string, BorrowingNet[]>>();
    for (const q of questions) {
        if (!q.isOpenEnd) continue;
        const ids = optionIdentities(q);
        q.options.forEach((o, i) => {
            const first = o.option_id[0];
            if (!o.isNet || !first || ids[i] === first) return;
            const qid = q.qid.toUpperCase();
            const byCode = out.get(qid) ?? new Map<string, BorrowingNet[]>();
            const code = first.toUpperCase();
            byCode.set(code, [...(byCode.get(code) ?? []), { head: ids[i], label: o.option }]);
            out.set(qid, byCode);
        });
    }
    return out;
}

/** A net label as a chip or a Nest member may store it: without " (Net)", in any case. */
const netLabelKey = (text: string | undefined): string =>
    labelKey((text ?? '').replace(/\s*\(Net\)\s*$/i, ''));

export function migrateBorrowedOeIds(
    questions: CrosstabQuestionDto[],
    state: OeIdState,
): OeIdMigration {
    const borrowed = borrowedOeCodes(questions);
    const unchanged: OeIdMigration = {
        liveFilters: state.liveFilters,
        customVariables: state.customVariables,
        weightScheme: state.weightScheme,
        ambiguousBannerKeys: [],
        changed: false,
    };
    if (borrowed.size === 0) return unchanged;

    const netsOf = (qid: string, code: string): BorrowingNet[] | undefined =>
        borrowed.get(String(qid).trim().toUpperCase())?.get(String(code).trim().toUpperCase());
    /** The first net that borrows the code: the option the old picker listed for it. */
    const shownAs = (qid: string, code: string): string | null =>
        netsOf(qid, code)?.[0]?.head ?? null;

    let changed = false;

    const liveFilters = state.liveFilters.map((f) => {
        if (!Array.isArray(f.optionIds)) return f;
        let touched = false;
        const optionIds = f.optionIds.map((id, j) => {
            const label = netLabelKey(f.optionLabels?.[j]);
            const net = netsOf(f.questionId, id)?.find((n) => netLabelKey(n.label) === label);
            if (!net) return id;
            touched = true;
            return net.head;
        });
        if (!touched) return f;
        changed = true;
        return { ...f, optionIds };
    });

    const bucketsOf = (buckets: VariableBucket[]): VariableBucket[] =>
        buckets.map((b) => {
            if (!Array.isArray(b.conditions)) return b;
            let touched = false;
            const conditions = b.conditions.map((c) => {
                if (c.operator !== 'eq' && c.operator !== 'neq') return c;
                const head = shownAs(c.questionId, c.value);
                if (!head) return c;
                touched = true;
                return { ...c, value: head };
            });
            if (!touched) return b;
            changed = true;
            return { ...b, conditions };
        });

    /** A Nest member key `QID=code` on a borrowed code names the net; any other key is kept. */
    const keyOf = (key: string): string => {
        const at = key.indexOf('=');
        const head = at > 0 ? shownAs(key.slice(0, at), key.slice(at + 1)) : null;
        if (!head) return key;
        changed = true;
        return `${key.slice(0, at)}=${head}`;
    };
    const memberOf = (m: NestMember): NestMember => ({
        ...m,
        key: keyOf(m.key),
        buckets: Array.isArray(m.buckets) ? bucketsOf(m.buckets) : m.buckets,
    });
    const recipeOf = (r: NestRecipe): NestRecipe => ({
        ...r,
        layers: r.layers.map((l) => ({ ...l, members: l.members.map(memberOf) })),
        ...(r.dropped ? { dropped: r.dropped.map((keys) => keys.map(keyOf)) } : {}),
        ...(r.names ? { names: r.names.map((n) => ({ ...n, keys: n.keys.map(keyOf) })) } : {}),
    });

    const customVariables = state.customVariables.map((cv) => {
        const was = changed;
        changed = false;
        const next: CustomVariable = {
            ...cv,
            buckets: Array.isArray(cv.buckets) ? bucketsOf(cv.buckets) : cv.buckets,
            ...(cv.nest ? { nest: recipeOf(cv.nest) } : {}),
        };
        const touched = changed;
        changed = was || touched;
        return touched ? next : cv;
    });

    let weightScheme = state.weightScheme;
    if (weightScheme) {
        const was = changed;
        changed = false;
        const next: WeightScheme = {
            ...weightScheme,
            variables: weightScheme.variables.map((v) => {
                if (!borrowed.has(v.variableId.toUpperCase())) return v;
                const targets: Record<string, number> = {};
                for (const [cat, share] of Object.entries(v.targets)) {
                    const head = shownAs(v.variableId, cat);
                    if (head) changed = true;
                    targets[head ?? cat] = share;
                }
                return { ...v, targets };
            }),
            ...(weightScheme.scope ? { scope: memberOf(weightScheme.scope) } : {}),
            ...(weightScheme.matchTo ? { matchTo: memberOf(weightScheme.matchTo) } : {}),
        };
        if (changed) weightScheme = next;
        changed = was || changed;
    }

    const ambiguousBannerKeys = Object.keys(state.bannerRenames).filter((key) => {
        const at = key.indexOf(':');
        return at > 0 && shownAs(key.slice(0, at), key.slice(at + 1)) !== null;
    });

    if (!changed) return { ...unchanged, ambiguousBannerKeys };
    return { liveFilters, customVariables, weightScheme, ambiguousBannerKeys, changed };
}

/** What the workspace says, once, when a crosstab opens with `ambiguousBannerKeys`. */
export function ambiguousOeRenameNotice(count: number): string {
    return count === 1
        ? 'A renamed open-end column in this crosstab used to rename a net and its first answer together. It now renames only the answer, so check the banner and rename the net again if needed.'
        : `${count} renamed open-end columns in this crosstab used to rename a net and its first answer together. They now rename only the answer, so check the banner and rename the nets again if needed.`;
}
